/**
 * Collects streamed search result `<message>` stanzas.
 *
 * The `search_streamed` ad-hoc command sends each hit as an individual
 * `<message>` stanza *before* the ad-hoc IQ response (XMPP stream ordering).
 * This collector captures those messages so the hook can return them once the
 * IQ response arrives with the metadata (queryid, found, sent).
 *
 * Protocol:
 *   <message from="wizard@host/wizard" to="user@host/res">
 *     <result xmlns="urn:slickshift:search:0" queryid="abc" index="0">
 *       <json xmlns="urn:xmpp:json:0">{"document":…,"highlight":…}</json>
 *     </result>
 *   </message>
 */

import { Element } from "@xmpp/xml"
import { SearchHit } from "./searchResultParser"

export const SEARCH_RESULT_NS = "urn:slickshift:search:0"

type PendingQuery = {
  hits: SearchHit[]
}

const pending = new Map<string, PendingQuery>()

/**
 * Register interest in a queryid before the IQ is sent.
 * The client generates the queryid and includes it in the ad-hoc form,
 * so we can start collecting before the request is made.
 */
export function startCollecting(queryid: string): void {
  pending.set(queryid, { hits: [] })
}

/**
 * Called from the stanza handler for every incoming `<message>` that carries
 * a `<result xmlns="urn:slickshift:search:0">`.
 *
 * Returns `true` if the stanza was consumed (should not be processed further).
 */
export function handleSearchResultStanza(stanza: Element): boolean {
  const result = stanza.getChild("result", SEARCH_RESULT_NS)
  if (!result) return false

  const queryid = result.attrs.queryid
  if (!queryid) return false

  const jsonEl = result.getChild("json", "urn:xmpp:json:0")
  if (!jsonEl) return false

  let hit: SearchHit
  try {
    hit = JSON.parse(jsonEl.getText())
  } catch {
    return true // malformed, but still consumed
  }

  const entry = pending.get(queryid)
  if (!entry) {
    // Ignore results for queries we didn't initiate
    return false
  }

  // Insert at the correct position using the index attribute
  const index = parseInt(result.attrs.index, 10)
  if (Number.isFinite(index)) {
    entry.hits[index] = hit
  } else {
    entry.hits.push(hit)
  }

  return true
}

/**
 * Retrieve and remove the collected hits for a queryid.
 * Called after the IQ response arrives (stream ordering guarantees all
 * messages were received before the IQ).
 */
export function collectResults(queryid: string): SearchHit[] {
  const entry = pending.get(queryid)
  pending.delete(queryid)
  if (!entry) return []
  // Filter out any sparse array holes
  return entry.hits.filter(Boolean)
}

/**
 * Clean up a query that was abandoned (e.g. request timed out).
 */
export function discardQuery(queryid: string): void {
  pending.delete(queryid)
}

/**
 * Check if a stanza is a streamed search result message.
 * Used by the stanza handler to short-circuit normal processing.
 */
export function isSearchResultMessage(stanza: Element): boolean {
  return (
    stanza.is("message") &&
    stanza.getChild("result", SEARCH_RESULT_NS) !== undefined
  )
}
